import React, { useEffect, useMemo, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Loader2 } from 'lucide-react';
import { MessageItem } from './MessageItem';
import { getDateSeparator } from '../utils/format';
import type { ChatDensity, ChatMessage } from '../types';
import { EmptyState } from '@/components/aurora/EmptyState';

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  density: ChatDensity;
  isGroup: boolean;
  currentUserId: string | null | undefined;
  onLoadMore: () => void;
  onReply: (m: ChatMessage) => void;
  onForward: (m: ChatMessage) => void;
  onReact: (messageId: string, emoji: string) => void;
  onPin: (messageId: string, isPinned: boolean) => void;
  onFavorite: (messageId: string, isFav: boolean) => void;
  onCreateTask: (m: ChatMessage) => void;
  onDelete: (messageId: string) => void;
}

// Task #1660 — lista messaggi virtualizzata con react-virtuoso.
// - `firstItemIndex` cresce verso il basso, decresce quando prepend (load more)
//   per mantenere la posizione di scroll stabile mentre carichiamo lo storico.
// - `startReached` callback per infinite scroll verso l'alto.
// - `followOutput="smooth"` segue automaticamente i nuovi messaggi quando
//   l'utente è già in fondo.
// - Render row include date separator inline (calcolato sul prev message,
//   raggruppamento temporale stile Slack).

const START_INDEX = 10_000;

export function MessageList(props: Props) {
  const {
    messages, loading, loadingMore, hasMore, density, isGroup, currentUserId,
    onLoadMore, onReply, onForward, onReact, onPin, onFavorite, onCreateTask, onDelete,
  } = props;

  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const firstIdxRef = useRef<number>(START_INDEX);
  const prevLenRef = useRef<number>(messages.length);

  // Quando arrivano messaggi PRIMA dei correnti (load more) decrementiamo
  // l'indice del primo elemento per non saltare la scroll position.
  useEffect(() => {
    const prev = prevLenRef.current;
    if (messages.length > prev && loadingMore === false) {
      // Heuristic: se l'ultimo messaggio è cambiato (append in fondo) lasciamo
      // firstItemIndex stabile; se solo i primi sono nuovi (prepend), shifta.
      const delta = messages.length - prev;
      const lastSame = messages[messages.length - 1]?.id === undefined ? false : true;
      if (lastSame && messages[messages.length - 1]?.id === messages[messages.length - 1]?.id) {
        // append normale (live o initial), no shift
      } else {
        firstIdxRef.current -= delta;
      }
    }
    prevLenRef.current = messages.length;
  }, [messages, loadingMore]);

  const itemContent = useMemo(() => (index: number, msg: ChatMessage) => {
    const localIdx = index - firstIdxRef.current;
    const prev = localIdx > 0 ? messages[localIdx - 1] : null;
    const sameDay = prev ? getDateSeparator(prev.created_at) === getDateSeparator(msg.created_at) : false;
    const sameSender = prev?.sender_id === msg.sender_id;
    const closeInTime = prev ? (new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime()) < 120_000 : false;
    const isContinuation = sameDay && sameSender && closeInTime;
    const isMine = msg.sender_id === currentUserId;
    const showDateSep = !sameDay || !prev;

    return (
      <div>
        {showDateSep && (
          <div className="flex items-center justify-center py-3">
            <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-[11px] font-medium text-gray-500 dark:text-gray-400 shadow-sm">
              {getDateSeparator(msg.created_at)}
            </div>
          </div>
        )}
        <MessageItem
          msg={msg}
          isMine={isMine}
          isContinuation={isContinuation && !showDateSep}
          isGroup={isGroup}
          density={density}
          currentUserId={currentUserId}
          index={localIdx}
          onReply={onReply}
          onForward={onForward}
          onReact={onReact}
          onPin={onPin}
          onFavorite={onFavorite}
          onCreateTask={onCreateTask}
          onDelete={onDelete}
        />
      </div>
    );
  }, [messages, currentUserId, isGroup, density, onReply, onForward, onReact, onPin, onFavorite, onCreateTask, onDelete]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="text-sm text-gray-400">Caricamento messaggi…</p>
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState
          illustration="no-messages"
          title="Nessun messaggio"
          subtitle="Inizia la conversazione: scrivi qui sotto per rompere il ghiaccio."
        />
      </div>
    );
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      className="flex-1"
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
      aria-label="Messaggi conversazione"
      data={messages}
      firstItemIndex={firstIdxRef.current}
      initialTopMostItemIndex={messages.length - 1}
      followOutput="smooth"
      startReached={() => { if (hasMore && !loadingMore) onLoadMore(); }}
      itemContent={itemContent}
      computeItemKey={(_idx, msg) => msg.id}
      components={{
        Header: loadingMore
          ? () => (
              <div className="flex justify-center py-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              </div>
            )
          : undefined,
      }}
    />
  );
}
